import type { LatLng } from './types';
import { haversineMeters } from './routing';
import { forEachChunk, yieldToMain } from './mainThread';

/**
 * Along-route amenities (fuel / toilets / rest) via the existing
 * `/v2/overpass` proxy. Separate cache from speed-camera POIs so a
 * camera refresh never wipes amenity data.
 */

export type AmenityKind = 'fuel' | 'toilet' | 'rest';

export interface Amenity {
  kind: AmenityKind;
  lat: number;
  lng: number;
  name?: string;
  /** Metres along the route polyline from route[0]. */
  alongMeters: number;
}

const AHEAD_LIMIT_M = 50_000;
const SAMPLE_EVERY_M = 8_000;
const MAX_SAMPLES = 7;
const AROUND_M = 900;
const TTL_MS = 20 * 60 * 1000;
const MAX_CACHE = 20;

interface CacheEntry {
  ts: number;
  data: Amenity[];
}

const cache = new Map<string, CacheEntry>();
let inFlight: AbortController | null = null;

function cacheKey(samples: LatLng[]): string {
  return samples
    .map((p) => `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`)
    .join('|');
}

function prune(): void {
  while (cache.size > MAX_CACHE) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

/** Cumulative distances along the polyline (index 0 → 0). */
export function routeAlongMeters(route: LatLng[]): number[] {
  const along = [0];
  for (let i = 1; i < route.length; i++) {
    along.push(along[i - 1]! + haversineMeters(route[i - 1]!, route[i]!));
  }
  return along;
}

/**
 * Sample points on the remaining route within the next 50 km, spaced
 * roughly every SAMPLE_EVERY_M so Overpass stays cheap.
 */
export function sampleRouteAhead(
  route: LatLng[],
  traveledMeters: number,
  aheadLimitM = AHEAD_LIMIT_M,
): LatLng[] {
  if (route.length < 2) return [];
  const along = routeAlongMeters(route);
  const total = along[along.length - 1] ?? 0;
  const start = Math.min(Math.max(0, traveledMeters), total);
  const end = Math.min(total, start + aheadLimitM);
  if (end <= start + 50) return [route[route.length - 1]!];

  const out: LatLng[] = [];
  let nextTarget = start;
  for (let i = 0; i < along.length; i++) {
    if (along[i]! < start) continue;
    if (along[i]! > end) break;
    if (along[i]! + 1 >= nextTarget || out.length === 0) {
      out.push(route[i]!);
      nextTarget = along[i]! + SAMPLE_EVERY_M;
      if (out.length >= MAX_SAMPLES) break;
    }
  }
  if (out.length === 0) out.push(route[route.length - 1]!);
  return out;
}

/** Project a point onto the route → along-metres (nearest vertex approx). */
function projectAlongRoute(p: LatLng, route: LatLng[], along: number[]): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < route.length; i++) {
    const d = haversineMeters(p, route[i]!);
    if (d < bestD) {
      bestD = d;
      best = along[i] ?? 0;
    }
  }
  return best;
}

function classifyElement(tags: Record<string, string> | undefined): AmenityKind | null {
  if (!tags) return null;
  if (tags.amenity === 'fuel') return 'fuel';
  if (tags.amenity === 'toilets') return 'toilet';
  if (
    tags.highway === 'services' ||
    tags.highway === 'rest_area' ||
    tags.amenity === 'rest_area'
  ) {
    return 'rest';
  }
  return null;
}

interface OverpassAmenityResponse {
  elements: Array<{
    type: 'node' | 'way';
    lat?: number;
    lon?: number;
    center?: { lat: number; lon: number };
    tags?: Record<string, string>;
  }>;
}

export async function fetchAmenitiesAlongRoute(
  route: LatLng[],
  traveledMeters: number,
): Promise<Amenity[]> {
  const samples = sampleRouteAhead(route, traveledMeters);
  if (samples.length === 0) return [];

  const key = cacheKey(samples);
  const hit = cache.get(key);
  const now = Date.now();
  if (hit && now - hit.ts < TTL_MS) return hit.data;

  if (inFlight) inFlight.abort();
  const ctl = new AbortController();
  inFlight = ctl;

  const parts = samples.flatMap((p) => [
    `node["amenity"="fuel"](around:${AROUND_M},${p.lat},${p.lng});`,
    `node["amenity"="toilets"](around:${AROUND_M},${p.lat},${p.lng});`,
    `node["highway"="services"](around:${AROUND_M},${p.lat},${p.lng});`,
    `node["highway"="rest_area"](around:${AROUND_M},${p.lat},${p.lng});`,
    `node["amenity"="rest_area"](around:${AROUND_M},${p.lat},${p.lng});`,
    `way["amenity"="fuel"](around:${AROUND_M},${p.lat},${p.lng});`,
    `way["highway"="services"](around:${AROUND_M},${p.lat},${p.lng});`,
    `way["highway"="rest_area"](around:${AROUND_M},${p.lat},${p.lng});`,
  ]);
  const query = `[out:json][timeout:25];(${parts.join('')});out center;`;

  try {
    const res = await fetch('/v2/overpass', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'data=' + encodeURIComponent(query),
      signal: ctl.signal,
    });
    if (!res.ok) {
      cache.set(key, { ts: now, data: [] });
      prune();
      return [];
    }
    const raw = await res.text();
    await yieldToMain();
    const json = JSON.parse(raw) as OverpassAmenityResponse;
    await yieldToMain();
    const along = routeAlongMeters(route);
    const total = along[along.length - 1] ?? 0;
    const start = Math.min(Math.max(0, traveledMeters), total);
    const end = Math.min(total, start + AHEAD_LIMIT_M);

    const seen = new Set<string>();
    const amenities: Amenity[] = [];
    await forEachChunk(json.elements ?? [], 40, (el) => {
      const kind = classifyElement(el.tags);
      if (!kind) return;
      const lat = el.type === 'node' ? el.lat : el.center?.lat;
      const lng = el.type === 'node' ? el.lon : el.center?.lon;
      if (lat === undefined || lng === undefined) return;
      const dedupe = `${kind}:${lat.toFixed(4)},${lng.toFixed(4)}`;
      if (seen.has(dedupe)) return;
      seen.add(dedupe);
      const alongM = projectAlongRoute({ lat, lng }, route, along);
      if (alongM < start - 200 || alongM > end + 200) return;
      const name = el.tags?.name?.trim();
      amenities.push({
        kind,
        lat,
        lng,
        alongMeters: alongM,
        ...(name ? { name } : {}),
      });
    });
    amenities.sort((a, b) => a.alongMeters - b.alongMeters);
    cache.set(key, { ts: now, data: amenities });
    prune();
    return amenities;
  } catch (err) {
    if ((err as Error).name === 'AbortError') return [];
    cache.set(key, { ts: now, data: [] });
    return [];
  } finally {
    if (inFlight === ctl) inFlight = null;
  }
}

/** Nearest amenity of each kind still ahead of the traveler. */
export function pickNextAmenities(
  amenities: Amenity[],
  traveledMeters: number,
): Partial<Record<AmenityKind, Amenity>> {
  const out: Partial<Record<AmenityKind, Amenity>> = {};
  for (const a of amenities) {
    if (a.alongMeters < traveledMeters - 50) continue;
    if (a.alongMeters - traveledMeters > AHEAD_LIMIT_M) continue;
    if (!out[a.kind]) out[a.kind] = a;
  }
  return out;
}
